/* eslint-disable no-nested-ternary */
import React, { useContext } from "react";

import classNames from "classnames";
import { observer } from "mobx-react";
import { lighten } from "polished";
import styled from "styled-components";

import JobAlertForm from "../../../components/JobAlertForm/JobAlertForm";
import ComponentContext from "../../../contexts/ComponentContext";
import { useStores } from "../../../contexts/StoreContext";
import breakpoints from "../../constants";
import Markdown from "../../styles/Markdown";
import Button from "../Button/Button";

const StyledContainer = styled.div`
  width: 100%;
  height: ${({ $containerHeight }) =>
    $containerHeight ? `${$containerHeight} !important` : "auto"};
  background-position: ${({ $backgroundPosition }) => $backgroundPosition};
  background-image: ${({ $backgroundImage }) =>
    $backgroundImage ? `url(${$backgroundImage})` : null};
  background-repeat: no-repeat;
  background-size: cover;
`;

const StyledWrapper = styled.div`
  color: ${({ $textColor, theme }) =>
    $textColor === "negative"
      ? theme.negativeTextColor
      : $textColor === "primary"
      ? theme.textColor
      : $textColor};
  backdrop-filter: blur(5px);
  transform: translate(
    ${({ $xOffset, $yOffset }) => `${$xOffset || "0"}, ${$yOffset || "0"}`}
  );
  background-color: ${({ $innerBackgroundColor }) =>
    $innerBackgroundColor || "#fff"};

  @media (min-width: ${breakpoints.DESKTOP}) {
    max-width: ${({ $innerWidth }) => $innerWidth || "495px"};
  }
`;

const StyledSignUp = styled.div`
  max-width: 670px;
  font-size: ${({ $inputFontSize }) => $inputFontSize || "13px"};

  p {
    margin: 0;
  }

  input {
    height: 100%;
    border: none;
  }
`;

const StyledButton = styled(Button)`
  height: 100%;
  width: 100%;
  background-color: ${({ theme, buttonColour }) =>
    buttonColour || theme.primaryColor};
  color: ${({ theme, buttonTextColour }) =>
    buttonTextColour || theme.negativeTextColor};
  border: none;
  outline: none;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;

  &:disabled {
    background-color: ${({ buttonColour, theme }) =>
      lighten(0.3, buttonColour || theme.primaryColor)};
  }

  &:hover,
  &:active,
  &:focus {
    background-color: ${({ buttonHoverColour, theme }) =>
      lighten(0.1, buttonHoverColour || theme.primaryColor)};
  }
`;

const StyledButtonWrapper = styled.div`
  width: ${({ $buttonWidth }) => $buttonWidth};
  height: ${({ height }) => height || "80px"};
`;

const horizontalAlignmentMap = {
  center: "center",
  left: "start",
  right: "end",
};

function SimpleJobAlert() {
  const { modalStore } = useStores();
  const { openModal } = modalStore;

  const {
    agreementContent,
    backgroundImage,
    backgroundPosition,
    buttonWidth = "120px",
    containerHeight,
    formPosition = "center",
    innerBackgroundColor = "transparent",
    innerWidth,
    privacyPolicyLink,
    formHeaderBackgroundColour = "",
    inputFontSize = "14px",
    inputHeight,
    text: jobAlertText,
    title,
    textColor = "primary",
    xOffset = "0",
    yOffset = "0",
    buttonText = "Sign Up",
    buttonColour,
    buttonTextColour,
    buttonHoverColour,
  } = useContext(ComponentContext);

  const onClick = () => {
    openModal({
      content: (
        <JobAlertForm
          agreementContent={agreementContent}
          privacyPolicyLink={privacyPolicyLink}
          formHeaderBackgroundColour={formHeaderBackgroundColour}
        />
      ),
      size: "md",
    });
  };

  const containerClassName = classNames(
    (formPosition === "left" || "right") && title
      ? "justify-content-between"
      : `justify-content-${horizontalAlignmentMap[formPosition]}`,
    title && formPosition === "right" && "flex-row",
    title && formPosition === "left" && "flex-row-reverse",
    "d-flex align-items-center",
    "position-relative",
    "px-2 px-lg-5",
    "shadow-lg"
  );

  const bgImageUrl = backgroundImage?.url || "";

  return (
    <StyledContainer
      $backgroundImage={bgImageUrl}
      $backgroundPosition={backgroundPosition}
      className={containerClassName}
      $containerHeight={containerHeight}
      data-testid="job-alert-container"
    >
      {title && <Markdown className="text-white">{title}</Markdown>}
      <StyledWrapper
        className={classNames("p-3 w-100 p-lg-5 job-alert-wrapper")}
        $innerBackgroundColor={innerBackgroundColor}
        $innerWidth={innerWidth}
        $textColor={textColor}
        $xOffset={xOffset}
        $yOffset={yOffset}
      >
        <StyledSignUp
          className="d-flex justify-content-between align-items-center"
          $inputFontSize={inputFontSize}
        >
          {jobAlertText && (
            <Markdown className="d-flex flex-column me-3">
              {jobAlertText}
            </Markdown>
          )}
          <StyledButtonWrapper $buttonWidth={buttonWidth} height={inputHeight}>
            <StyledButton
              className="text-uppercase"
              height="100%"
              onClick={onClick}
              type="button"
              buttonColour={buttonColour}
              buttonTextColour={buttonTextColour}
              buttonHoverColour={buttonHoverColour}
            >
              {buttonText}
            </StyledButton>
          </StyledButtonWrapper>
        </StyledSignUp>
      </StyledWrapper>
    </StyledContainer>
  );
}

export default observer(SimpleJobAlert);
